<!-- src/components/Inventario/MateriaPrima/PublicMateriaPrima.vue -->
<template>
  <div class="pmp-screen">
    <PMPHeader
      v-if="currentStep < 6"
      brand="Consumo de Materia Prima"
      :step="currentStep"
      :total-steps="6"
    />

    <main class="pmp-content" :class="{ 'pmp-content--success': currentStep === 6 }">
      <!-- Paso 1 -->
      <PMPStep1Registro
        v-if="currentStep === 1"
        v-model="form"
        :operadores="operadores"
        :maquinas="maquinas"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <!-- Paso 2 -->
      <PMPStep2Produccion
        v-else-if="currentStep === 2"
        v-model="form"
        :productos="productos"
        :colores="colores"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <!-- Paso 3 -->
      <PMPStep3Material
        v-else-if="currentStep === 3"
        v-model="form"
        :proveedores="proveedores"
        :unidades="unidades"
        :errors="errors"
        @field-change="handleFieldChange"
        @add-tpu="handleAddTpu"
        @remove-tpu="handleRemoveTpu"
      />

      <!-- Paso 4 -->
      <PMPStep4Comentarios
        v-else-if="currentStep === 4"
        v-model="form"
        :unidades="unidades"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <!-- Paso 5 -->
      <PMPStep5Resumen v-else-if="currentStep === 5" :form="form" :format-gr="formatGr" />

      <!-- Paso 6 -->
      <PMPStep6Enviado v-else-if="currentStep === 6" :form="form" />
    </main>

    <PMPBottomBar
      v-if="currentStep < 6"
      :show-back="canGoBack"
      :next-label="buttonLabel"
      :next-icon="buttonIcon"
      :loading="isSubmitting"
      @back="goBack"
      @next="handleNext"
    />

    <PMPBottomBar
      v-else
      next-label="Crear otro registro"
      next-icon="bi-arrow-repeat"
      @next="handleNewRecord"
    />
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";

import PMPHeader from "./components/PMPHeader.vue";
import PMPBottomBar from "./components/PMPBottomBar.vue";
import PMPStep1Registro from "./steps/PMPStep1Registro.vue";
import PMPStep2Produccion from "./steps/PMPStep2Produccion.vue";
import PMPStep3Material from "./steps/PMPStep3Material.vue";
import PMPStep4Comentarios from "./steps/PMPStep4Comentarios.vue";
import PMPStep5Resumen from "./steps/PMPStep5Resumen.vue";
import PMPStep6Enviado from "./steps/PMPStep6Enviado.vue";

import Swal from "sweetalert2";
import { useMateriaPrimaForm } from "./composables/useMateriaPrimaForm";
import { crearReporteMateriaPrima } from "./services/materiaPrimaApi";
import {
  getMaquinasPublic,
  getProductosPublic,
  getPigmentosPublic,
  getOperadoresPublic,
  getTPUsPublic,
} from "@/utils/api_materia_prima";

import "./styles/materia-prima.css";

const {
  form,
  currentStep,
  canGoBack,
  parseNumber,
  formatKg,
  formatGr,
  validateStep,
  buildPayload,
  resetForm,
  completeDraft,
  addTpuItem,
  removeTpuItem,
} = useMateriaPrimaForm();

const isSubmitting = ref(false);

/**
 * Temporal:
 * true = permite navegar aunque los steps reales aún no estén conectados.
 * false = activa validación real por paso.
 */
const allowStepBypass = false;

const errors = ref({
  fecha: false,
  hora: false,
  operador: false,
  maquina: false,

  producto: false,
  color: false,
  cantidadPigmentoGr: false,

  tpuItems: [],

  molidaCantidad: false,
  molidaUnidad: false,
  comentarios: false,
});

const operadores = ref([]);
const maquinas = ref([]);
const productos = ref([]);
const colores = ref([]);
const proveedores = ref([]);

const unidades = [
  { value: "kg", label: "kg" },
  { value: "gr", label: "gr" },
];

const cargarCatalogos = async () => {
  try {
    const [ops, maqs, prods, cols, tpus] = await Promise.all([
      getOperadoresPublic(),
      getMaquinasPublic(),
      getProductosPublic(),
      getPigmentosPublic(),
      getTPUsPublic(),
    ]);

    // ✅ OPERADORES: el backend devuelve "nombre_completo" no "nombre"
    operadores.value = ops.map((op) => ({
      value: op.id,
      label: op.nombre_completo,
    }));

    // ✅ MAQUINAS: el backend devuelve "nombre" - correcto
    // Orden natural (IMMI 01, 02, ..., 10) en vez del orden por id que envía el backend
    maquinas.value = [...maqs]
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es", { numeric: true }))
      .map((m) => ({
        value: m.id,
        label: m.nombre,
      }));

    // ✅ PRODUCTOS: el backend devuelve "nombre" - correcto
    productos.value = prods.map((p) => ({
      value: p.id,
      label: p.nombre,
    }));

    // ✅ COLORES: el backend devuelve "nombre" y "hexadecimal"
    colores.value = cols.map((c) => ({
      value: c.id,
      label: c.nombre,
      hex: c.hexadecimal, // 👈 el backend usa "hexadecimal" no "hex"
    }));

    // ✅ TPUs: el backend devuelve "nombre"
    proveedores.value = tpus.map((t) => ({
      value: t.id,
      label: t.nombre,
    }));
  } catch (error) {
    console.error("Error al cargar catálogos:", error);
  }
};

onMounted(() => {
  cargarCatalogos();
});

const buttonLabel = computed(() => {
  if (isSubmitting.value) return "Enviando...";
  if (currentStep.value === 5) return "Aceptar y enviar";
  return "Siguiente";
});

const buttonIcon = computed(() => {
  if (currentStep.value === 5) return "bi-send-check";
  return "bi-arrow-right";
});

function goBack() {
  if (currentStep.value > 1) {
    currentStep.value -= 1;
  }
}

async function handleNext() {
  if (!allowStepBypass && !validateCurrentStep()) {
    return;
  }

  if (currentStep.value < 5) {
    currentStep.value += 1;
    return;
  }

  await submitReport();
}

async function submitReport() {
  try {
    isSubmitting.value = true;

    const payload = buildPayload();
    const [response] = await Promise.all([
      crearReporteMateriaPrima(payload),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);

    form.folio = response?.folio || generarFolioTemporal();
    form.submittedAt = response?.submittedAt || new Date().toISOString();

    currentStep.value = 6;
    completeDraft();
  } catch (error) {
    console.error("Error al enviar reporte de materia prima:", error);

    Swal.fire({
      icon: "error",
      title: "Error al enviar",
      text: "No se pudo enviar el reporte. Verifica tu conexión e intenta de nuevo.",
    });
  } finally {
    isSubmitting.value = false;
  }
}

function generarFolioTemporal() {
  const year = String(new Date().getFullYear()).slice(-2);
  const random = Math.floor(1000 + Math.random() * 9000);
  return `CMP-${year}-${random}`;
}

function handleNewRecord() {
  resetForm();
  resetErrors();
}

function resetErrors() {
  Object.keys(errors.value).forEach((key) => {
    errors.value[key] = key === "tpuItems" ? [] : false;
  });
}

function handleFieldChange({ field, index }) {
  if (!field) return;

  if (field.startsWith("tpuItems.")) {
    clearTpuFieldError(field, index);
    return;
  }

  errors.value[field] = false;

  if (field === "molidaCantidad" && form.molidaCantidad === "") {
    errors.value.molidaUnidad = false;
  }
}

function clearTpuFieldError(field, index) {
  if (index == null || !errors.value.tpuItems?.[index]) return;

  const key = field.split(".")[1];
  errors.value.tpuItems[index][key] = false;
}

function handleAddTpu() {
  addTpuItem();

  errors.value.tpuItems.push({
    proveedor: false,
    cantidad: false,
    unidad: false,
  });
}

function handleRemoveTpu(id) {
  const index = form.tpuItems.findIndex((item) => item.id === id);

  removeTpuItem(id);

  if (index >= 0) {
    errors.value.tpuItems.splice(index, 1);
  }
}

function validateCurrentStep() {
  resetErrors();

  if (currentStep.value === 1) {
    errors.value.fecha = !form.fecha;
    errors.value.hora = !form.hora;
    errors.value.operador = !form.operador;
    errors.value.maquina = !form.maquina;
  }

  if (currentStep.value === 2) {
    errors.value.producto = !form.producto;
    errors.value.color = !form.color;
    errors.value.cantidadPigmentoGr =
      form.cantidadPigmentoGr === "" || parseNumber(form.cantidadPigmentoGr) < 0;
  }

  if (currentStep.value === 3) {
    errors.value.tpuItems = form.tpuItems.map((item) => ({
      proveedor: !item.proveedor,
      cantidad: item.cantidad === "" || parseNumber(item.cantidad) <= 0,
      unidad: !item.unidad,
    }));
  }

  if (currentStep.value === 4) {
    errors.value.molidaCantidad =
      form.molidaCantidad === "" || parseNumber(form.molidaCantidad) < 0;
    errors.value.molidaUnidad = !form.molidaUnidad;
  }

  return validateStep(currentStep.value);
}
</script>
